/**
 * Data-model entities and fields — the Widget Builder's Data step (CT-5191 …
 * CT-5195).
 *
 * These live on the **Data Studio** gateway, not the Dashboard one:
 * `/v0/datamodel/composite-entity/list` and `/v0/datamodel/entity/field/list`.
 * Same envelope, different aggregate, and its own message codes (`DST100` for
 * the composite list, `DST020` for fields) — which is why nothing here checks
 * for a `DSW` prefix.
 *
 * The catalog is narrower than the pickers were designed against: it has no
 * integration, no PII flag and no "featured" concept. Those degrade to empty
 * rather than being invented, and `featured` is derived here (see below) so no
 * component has to know the catalog lacks it.
 *
 * ## Why composites and not entities
 *
 * A widget's data source points at a **composite**
 * (`dataSource.entity.compositeEntityUuid`), so the composite is what the
 * author is really choosing. This used to list `entity/list` — the member
 * tables — and recover the composite from `composite_memberships[0]`, which
 * was wrong in three separate ways, all of them measured against dev:
 *
 *   - Members that are not their composite's `PRIMARY` were offered and could
 *     not back anything: `DSW018 Fields [...] are not fields of the chosen
 *     entity` on save, with no way to tell from the card.
 *   - `composite_memberships[0]` is a guess. A table that belongs to two
 *     composites got whichever the backend happened to return first.
 *   - It did not invert. A widget that stored only its composite had no member
 *     to ask for columns on reopen, because `entity/field/list` answers
 *     `DST021 Parent entity … not found` for a composite uuid.
 *
 * `composite-entity/list` carries both halves on the same row —
 * `compositeEntityUuid` to store, `primaryEntityUuid` to ask for fields — so
 * the mapping is read instead of inferred, and it goes both ways.
 *
 * A composite with no primary member has no fields to offer and is dropped
 * here, which is what an empty `id` would mean downstream.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { cachedRequest, invalidate } from './requestCache.js';
import { apiDataStudio, DATAMODEL_API_PREFIX } from './apiClient.js';

/** How many entities the Data step offers before the picker modal is opened. */
const FEATURED_COUNT = 4;

const DEFAULT_PAGE_SIZE = 100;

/**
 * Physical type → the four the UI branches on.
 *
 * Every operator set, value control and type pill keys off this, so a type that
 * arrived unmapped would silently give a column no operators at all. Anything
 * unrecognised falls back to `text`, which is the only type whose operators
 * work for an opaque value.
 */
const UI_TYPE_BY_DATA_TYPE = Object.freeze({
  TEXT: 'text', VARCHAR: 'text', CHAR: 'text', STRING: 'text',
  UUID: 'text', JSON: 'text', JSONB: 'text',
  INTEGER: 'number', BIGINT: 'number', SMALLINT: 'number',
  NUMERIC: 'number', DECIMAL: 'number', FLOAT: 'number',
  DOUBLE: 'number', REAL: 'number',
  TIMESTAMP: 'date', TIMESTAMPTZ: 'date', DATE: 'date', TIME: 'date',
  BOOLEAN: 'boolean', BOOL: 'boolean',
});

/**
 * Composite entity → the card the Data step and the picker modal render.
 *
 * **The picker offers composites, not their member tables.** A widget is
 * stored against `compositeEntityUuid`, and every field uuid it sends is
 * validated against that composite — so the composite is the thing being
 * chosen, and listing members was asking the author to pick a part while the
 * whole is what gets saved. Three failures came out of that:
 *
 *   - Members that are not the composite's `PRIMARY` were on the list and
 *     could not back anything. `DSW018 Fields [...] are not fields of the
 *     chosen entity` on every save — measured on the same composite, where a
 *     PRIMARY member's field is accepted and an `EXTENSION` member's is not.
 *   - The composite was **guessed**, from `composite_memberships[0]`. A table
 *     in two composites got whichever came first.
 *   - Nothing could go the other way. Reopening a widget that stored only its
 *     composite had no member to ask for columns, and `entity/field/list`
 *     refuses a composite uuid — `DST021 Parent entity … not found`.
 *
 * `composite-entity/list` answers with both halves at once, which is what
 * makes this work: `compositeEntityUuid` to store, `primaryEntityUuid` to ask
 * for fields.
 *
 * `id` is the **composite's** uuid — the card's identity. Two composites can
 * share one primary table (dev has "model 14 09 26" and "SmkM Composite …"
 * on the same one), so keying the card by its primary made them one option:
 * one React `key`, both cards selected at once, and `find` by `id` storing
 * the first composite the catalog returned — the very guess this replaced.
 * `primaryEntityUuid` travels beside it and is what `entity/field/list` takes
 * and what the rest of the builder carries as `entityRef`. The name and the
 * description come from the composite, because that is what the author is
 * choosing.
 */
export const mapApiCompositeEntityToUi = (raw = {}) => ({
  // The composite: unique, so it is what tells one card from another.
  id: raw.compositeEntityUuid ?? '',
  // Read, no longer inferred.
  compositeEntityUuid: raw.compositeEntityUuid ?? '',
  // The uuid every column request needs, and what the form stores as `entityRef`.
  primaryEntityUuid: raw.primaryEntityUuid ?? '',
  code: raw.compositeCode ?? '',
  name: raw.compositeName ?? raw.compositeCode ?? '',
  description: raw.businessDescription ?? raw.llmDescription ?? '',
  // Member tables, not columns: the composite catalog counts the tables it
  // joins and has no column total. Saying "5 tables" is true; reusing the
  // column label for it would not be.
  memberCount: raw.listSummary?.data?.total_tables ?? 0,
  // A composite is governed once it has been published.
  governed: raw.draftPublishedStatus === 'PUBLISHED',
  entityType: '',
  updatedAt: raw.updatedAt ?? null,
  // No counterpart in the data-model catalog. The picker hides both when they
  // are empty rather than showing a blank badge.
  integration: '',
  hasPii: false,
  alsoIntegrations: [],
  // Set by `listEntities`, which is the only place that can see the whole page.
  featured: false,
});

/** Field → the column the filters, Group by and Columns-to-expose steps render. */
export const mapApiEntityFieldToUi = (raw = {}) => ({
  // The widget's filters and exposed columns reference fields by uuid, not by
  // name — `Every filter needs a fieldUuid`, and the endpoint checks the field
  // really belongs to the chosen composite.
  fieldUuid: raw.entityFieldUuid ?? '',
  internalName: raw.fieldCode ?? '',
  displayName: raw.fieldName ?? raw.fieldCode ?? '',
  description: raw.businessDescription ?? raw.llmDescription ?? '',
  type: UI_TYPE_BY_DATA_TYPE[String(raw.dataType ?? '').toUpperCase()] ?? 'text',
  // The stored type, kept alongside the four-way UI one. The presentation
  // contract distinguishes types the UI deliberately collapses — `UUID` and
  // `TEXT` are both `text` to a filter row, and `DATE` and `TIMESTAMP` are both
  // `date`, but a binding slot accepts one and rejects the other.
  dataType: String(raw.dataType ?? '').toUpperCase(),
  // Carried, not filtered on here: a filter may narrow by `created_at`, and
  // only the **projection** refuses one — a system field among a record set's
  // columns is `DSW018 UI_QUERY_COMPONENT_FIELD_INVALID`, which is why the
  // exposed-columns picker reads this flag.
  isSystem: raw.fieldRole === 'SYSTEM_CONTROL',
  // The entity's public record uuid — `<base>_uuid`, seeded as a UUID under
  // `SYSTEM_CONTROL` and hidden from forms. It is the only field a `row_key`
  // binding accepts (the slot's `x-data-types` is `["UUID"]` alone), so the
  // record-set types need it in the query even though nobody picks it.
  isRecordKey: raw.fieldRole === 'SYSTEM_CONTROL'
    && String(raw.dataType ?? '').toUpperCase() === 'UUID'
    && String(raw.fieldCode ?? '').endsWith('_uuid'),
  isRequired: raw.isRequired ?? false,
});

/**
 * List the sources a widget can be built from — the composites.
 *
 * The catalog has no `featured` flag, so the Data step's short list is the four
 * most recently updated composites — explainable, stable between renders, and
 * it invents no concept the backend does not have. The picker modal still lists
 * everything.
 *
 * **Keyset pagination, not page numbers.** This endpoint ignores `page`
 * entirely: it answers with `meta.nextCursor`, which the caller sends back as
 * `nextCursor` to advance, and `null` marks the last page. Passing `page` and
 * reading `hasNext` walks the *same* first page forever — the backend keeps
 * answering `hasNext: true` with the same cursor, because it was never asked
 * to move.
 */
async function fetchEntities({ search, pageSize, nextCursor } = {}) {
  const response = await apiDataStudio.post(`${DATAMODEL_API_PREFIX}/composite-entity/list`, {
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    ...(search ? { searchKeyword: search } : {}),
    ...(nextCursor ? { nextCursor } : {}),
  });

  const { body, meta } = validateApiResponse(response, 'Could not load entities.');
  const entities = (body ?? [])
    .map(mapApiCompositeEntityToUi)
    // Half the composites in dev have no primary member. Nothing can be built
    // on one: there is no entity to ask for columns, so every picker after the
    // first step would be empty. Offering it is offering a dead end.
    .filter((entity) => entity.id && entity.primaryEntityUuid);

  const featured = new Set(
    [...entities]
      .sort((a, b) => Date.parse(b.updatedAt ?? 0) - Date.parse(a.updatedAt ?? 0))
      .slice(0, FEATURED_COUNT)
      .map((entity) => entity.id),
  );

  return {
    entities: entities.map((entity) => ({ ...entity, featured: featured.has(entity.id) })),
    meta: normalizePagedMeta({ totalItems: entities.length, ...(meta ?? {}) }),
  };
}

/** A runaway cursor loop is worse than a short list; 20 pages is 2000 fields. */
const MAX_FIELD_PAGES = 20;

/**
 * List one entity's fields — **all of them**.
 *
 * `entityUuid` is required by the endpoint — without it the answer is a
 * `VALIDATION_ERROR`, not an empty list.
 *
 * **It pages, and its default is 20.** Asking once returned the first twenty
 * fields and `hasNext: true`, which every caller here read as the whole
 * entity: a 30-field entity lost ten, and among them the `<base>_uuid` the
 * record-set types bind their `row_key` to — so a table over it saved with no
 * presentation contract and `publish` answered
 * `DSW012 UI_WIDGET_PRESENTATION_CONFIG_REQUIRED {missing: bindings}`. The
 * missing columns were also absent from the filter, grouper and
 * columns-to-expose pickers, with nothing on screen to say so.
 *
 * Keyset, like `entity/list`: `meta.nextCursor` advances and `null` ends it.
 * The repeated-cursor guard is the same one `listAllEntities` carries — a
 * backend that answers with the cursor it was given would otherwise spin here
 * forever.
 */
async function fetchEntityFields({ entityUuid } = {}) {
  const raw = [];
  let nextCursor = null;

  for (let page = 0; page < MAX_FIELD_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- a cursor page depends on the one before it.
    const response = await apiDataStudio.post(`${DATAMODEL_API_PREFIX}/entity/field/list`, {
      entityUuid,
      pageSize: DEFAULT_PAGE_SIZE,
      ...(nextCursor ? { nextCursor } : {}),
    });
    const { body, meta } = validateApiResponse(response, 'Could not load the entity fields.');
    raw.push(...(body ?? []));

    const cursor = meta?.nextCursor ?? null;
    if (!cursor || cursor === nextCursor) break;
    nextCursor = cursor;
  }

  return {
    columns: raw
      // The physical column order is what someone reading the entity expects.
      .sort((a, b) => (a.ordinalPosition ?? 0) - (b.ordinalPosition ?? 0))
      .map(mapApiEntityFieldToUi),
  };
}

/** How many field maps one request asks for. The endpoint's own ceiling. */
const FIELD_MAP_PAGE_SIZE = 500;

/** A runaway cursor loop is worse than a short list; 20 pages is 10,000 maps. */
const MAX_FIELD_MAP_PAGES = 20;

/**
 * One row of `composite-entity/field-map/list` — a field the composite exposes
 * under its own label, with its origin already resolved (CT-5480).
 *
 * **snake_case, and everything joined server-side.** The row carries the
 * member (`member_uuid`, `member_role`), the source table and the source
 * field, so nothing here has to be looked up against a second response.
 *
 * `display_name` is the **read** side and is what to show: the catalog has
 * already resolved it, falling back to the source field's own name when
 * nobody set an override. `display_label_override` is the write side and is
 * `null` on every row nobody renamed — reading it first shows blank labels.
 */
export const mapApiCompositeFieldToUi = (raw = {}) => ({
  fieldMapUuid: raw.field_map_uuid ?? raw.fieldMapUuid ?? '',
  memberUuid: raw.member_uuid ?? raw.memberUuid ?? '',
  // The uuid the widget payload sends, nested under the source field — there
  // is no flat one on a real row.
  fieldUuid: raw.source_field?.entity_field_uuid ?? raw.sourceField?.entityFieldUuid ?? '',
  alias: raw.composite_field_alias ?? raw.compositeFieldAlias ?? '',
  label: raw.display_name
    ?? raw.displayName
    ?? raw.display_label_override
    ?? raw.displayLabelOverride
    ?? '',
  section: raw.display_section ?? raw.displaySection ?? '',
  sortOrder: raw.sort_order ?? raw.sortOrder ?? 0,
  isVisible: raw.is_visible_in_ui ?? raw.isVisibleInUi ?? true,
  // A soft-deleted mapping. `includeRemoved` defaults to false, so these do
  // not normally arrive; the guard is for a caller that asks for them.
  isRemoved: Boolean(raw.marked_deleted ?? raw.markedDeleted ?? 0),
  // `MEMBER` or `RELATED_COMPOSITE`. Only members exist in dev today; a
  // related composite also carries `relationship_resolution`.
  sourceType: raw.source_type ?? raw.sourceType ?? 'MEMBER',
  origin: {
    memberUuid: raw.member_uuid ?? raw.memberUuid ?? '',
    role: raw.member_role ?? raw.memberRole ?? '',
    entityUuid: raw.source_table?.entity_uuid ?? raw.sourceTable?.entityUuid ?? '',
    tableCode: raw.source_table?.entity_code ?? raw.sourceTable?.entityCode ?? '',
    tableName: raw.source_table?.entity_name ?? raw.sourceTable?.entityName ?? '',
  },
});

/**
 * The fields a composite exposes — its field mapping (CT-5480).
 *
 * **`POST /v0/datamodel/composite-entity/field-map/list`** is the source for
 * the fields a widget may show. It replaced `entity/field/list`, which listed
 * a table's physical columns: the composite's mapping is what the product
 * exposes, and only it says where each field comes from.
 *
 * `entity/field/list` has not disappeared from the builder, and should not:
 * the record-set types bind `row_key` to the primary's `<base>_uuid`, nobody
 * maps a record uuid, and without it `publish` answers
 * `DSW012 {missing: bindings}`. That one lookup is for a column the author
 * never sees or picks — see `resolveMappedColumns`.
 *
 * Keyset, like the rest of the catalog: `limit` up to 500 and an opaque
 * `nextCursor`. Removed mappings are excluded by the endpoint
 * (`includeRemoved` defaults to false) and again here, so a caller that turns
 * the flag on does not silently offer them.
 *
 * ## What this returns on dev today
 *
 * Five of the 44 composites have any mapping at all, and Employee's is a
 * single field. A composite with none answers an empty list, which is a valid
 * state, not an error — the caller decides what to do with it.
 *
 * @param {string} compositeEntityUuid
 * @returns {Promise<{ fields: object[], members: object[] }>}
 */
async function fetchCompositeFields(compositeEntityUuid) {
  const raw = [];
  let nextCursor = null;

  for (let page = 0; page < MAX_FIELD_MAP_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- a cursor page depends on the one before it.
    const response = await apiDataStudio.post(
      `${DATAMODEL_API_PREFIX}/composite-entity/field-map/list`,
      {
        compositeEntityUuid,
        limit: FIELD_MAP_PAGE_SIZE,
        ...(nextCursor ? { nextCursor } : {}),
      },
    );
    const { body, meta } = validateApiResponse(response, 'Could not load the composite fields.');
    raw.push(...(body ?? []));

    const cursor = meta?.nextCursor ?? null;
    if (!cursor || cursor === nextCursor) break;
    nextCursor = cursor;
  }

  const fields = raw
    .map(mapApiCompositeFieldToUi)
    .filter((field) => field.fieldUuid && field.isVisible && !field.isRemoved)
    // The endpoint answers newest first; the composite's own order is what
    // someone reading its Fields tab in Data Studio sees.
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Derived from the rows, not fetched: every mapping names its member, and a
  // member with nothing mapped contributes no column to show.
  const members = [...new Map(fields.map((field) => [field.origin.memberUuid, field.origin])).values()];

  return { fields, members };
}

/**
 * One relationship a composite can navigate, from its own point of view
 * (CT-5529).
 *
 * `POST /v0/datamodel/composite-entity/relationship/list` answers the
 * relationships of the composite you ask for, already **resolved relative to
 * it**: the row names the composite on the other side and the direction to
 * travel, so nothing here has to work out which end it is standing on.
 *
 * That relative resolution is what the widget payload needs verbatim —
 * `compositeRelationshipUuid` and `relativeDirection` go straight into the
 * query's join, and `otherCompositeEntityUuid` says whose fields to offer.
 */
export const mapApiCompositeRelationshipToUi = (raw = {}) => ({
  relationshipUuid: raw.compositeRelationshipUuid ?? '',
  // `SOURCE_TO_TARGET` or `TARGET_TO_SOURCE`, already flipped for the
  // composite that asked. Taken as given rather than derived from the
  // source/target uuids, which are null on a member-backed relationship.
  direction: raw.relativeDirection ?? '',
  compositeEntityUuid: raw.otherCompositeEntityUuid ?? '',
  compositeName: raw.otherCompositeEntityName ?? '',
  cardinality: raw.cardinality ?? '',
  status: raw.status ?? '',
  // The backend's own two signals that this hop can be taken.
  canNavigate: raw.canNavigate === true,
  routeStatus: raw.routeValidity?.status ?? '',
});

/**
 * The composites this one can reach, and how (CT-5529).
 *
 * Only the hops the catalog says are navigable come back: `canNavigate` and a
 * `routeValidity` of `VALID`. **`status` is deliberately not filtered on** —
 * every relationship in dev is `DRAFT` while its route is `VALID` and
 * `canNavigate` is true, so refusing drafts would refuse all of them. The two
 * flags are the backend's own answer to "can this be traversed"; the lifecycle
 * state is a different question, and guessing that a draft is untraversable
 * would silently empty the picker.
 *
 * A failure is not fatal to the Data step: the caller falls back to the
 * composite's own fields, which is what it showed before this existed.
 *
 * @param {string} compositeEntityUuid
 * @returns {Promise<{ relationships: object[] }>}
 */
async function fetchCompositeRelationships(compositeEntityUuid) {
  const response = await apiDataStudio.post(
    `${DATAMODEL_API_PREFIX}/composite-entity/relationship/list`,
    { compositeEntityUuid },
  );
  const { body } = validateApiResponse(response, 'Could not load the composite relationships.');

  const relationships = (body ?? [])
    .map(mapApiCompositeRelationshipToUi)
    .filter((relationship) => relationship.relationshipUuid
      && relationship.compositeEntityUuid
      && relationship.direction
      && relationship.canNavigate
      && relationship.routeStatus === 'VALID');

  /*
   * Two relationships can reach the same composite by different routes. The
   * picker offers one block per composite, so one route has to win, and it
   * is the **first** — `new Map(entries)` would keep the last, which reads
   * the same and is not: the payload would change under a caller whose only
   * difference was the order the catalog answered in.
   */
  const firstPerNeighbour = new Map();
  relationships.forEach((relationship) => {
    if (!firstPerNeighbour.has(relationship.compositeEntityUuid)) {
      firstPerNeighbour.set(relationship.compositeEntityUuid, relationship);
    }
  });

  return { relationships: [...firstPerNeighbour.values()] };
}

/* ── Cached entry points ──────────────────────────────────────────────────
 *
 * The data model is a catalog: several hooks read the same rows, and the
 * builder's pickers unmount and remount every time the author changes tab or
 * changes source. Reading these through `cachedRequest` is what turns that
 * into one request instead of one per mount — see `requestCache.js` for the
 * measurement that prompted it (CT-5579).
 *
 * The key carries the parameters, so two searches stay two requests and one
 * entity's fields are never served for another.
 */

/** @see fetchEntities */
export const listEntities = (params = {}) => cachedRequest(
  `entities|${params.search ?? ''}|${params.pageSize ?? ''}|${params.nextCursor ?? ''}`,
  () => fetchEntities(params),
);

/** @see fetchEntityFields */
export const listEntityFields = (params = {}) => cachedRequest(
  `entityFields|${params.entityUuid ?? ''}`,
  () => fetchEntityFields(params),
);

/** @see fetchCompositeFields */
export const getCompositeFields = (compositeEntityUuid) => cachedRequest(
  `compositeFields|${compositeEntityUuid ?? ''}`,
  () => fetchCompositeFields(compositeEntityUuid),
);

/** @see fetchCompositeRelationships */
export const listCompositeRelationships = (compositeEntityUuid) => cachedRequest(
  `compositeRelationships|${compositeEntityUuid ?? ''}`,
  () => fetchCompositeRelationships(compositeEntityUuid),
);

/**
 * Forget the entity list, so the next read goes to the network.
 *
 * What the pickers' `refetch` calls. Without it "try again" after a failed
 * load would hand back the cached answer and look like nothing happened —
 * which is only half true, since a rejection evicts itself, but a stale
 * success would not.
 */
export const invalidateEntities = () => invalidate('entities|');

/** Forget everything derived from one composite: its fields and its joins. */
export const invalidateCompositeEntity = (compositeEntityUuid) => {
  invalidate(`compositeFields|${compositeEntityUuid ?? ''}`);
  invalidate(`compositeRelationships|${compositeEntityUuid ?? ''}`);
  invalidate(`entityFields|${compositeEntityUuid ?? ''}`);
};
